/**
 * LightSensorModalClassic — Monolithic light sensor modal template.
 *
 * Read-only light sensor display panel:
 * - Lux: large lux value display with sun icon
 * - Level: descriptive light level indicator (Dark/Dim/Normal/Bright)
 *
 * No commands — purely observational.
 */
import { useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './LightSensorModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const LIGHT_SENSOR_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Lux Section ──
  { id: 'lux', label: 'Lux Reading', icon: 'light_mode', slots: [
    { id: 'lux-label', label: 'Title "ILLUMINANCE"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'lux-icon', label: 'Sun Icon', editableProps: ['fontSize', 'color'] },
    { id: 'lux-value', label: 'Lux Number (large)', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'lux-unit', label: 'Unit "lux"', editableProps: ['fontSize', 'color'] },
  ]},
  // ── Level Section ──
  { id: 'level', label: 'Light Level', icon: 'brightness_6', slots: [
    { id: 'level-label', label: 'Title "LIGHT LEVEL"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'level-badge', label: 'Level Badge', editableProps: ['background', 'color', 'borderRadius', 'padding', 'fontSize', 'fontWeight'] },
    { id: 'level-bar-fill', label: 'Level Bar Fill', editableProps: ['background', 'height', 'borderRadius'] },
    { id: 'level-desc', label: 'Level Description Text', editableProps: ['fontSize', 'color'] },
  ]},
]

const DEFAULT_SECTION_ORDER = LIGHT_SENSOR_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  const v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Light level thresholds
// ---------------------------------------------------------------------------

type LightLevel = { label: string; desc: string; color: string; percent: number }

function getLightLevel(lux: number): LightLevel {
  if (lux < 10)  return { label: 'Dark',   desc: 'Very little or no light',      color: '#475569', percent: 5 }
  if (lux < 100) return { label: 'Dim',    desc: 'Low ambient light',             color: '#a16207', percent: 25 }
  if (lux < 500) return { label: 'Normal', desc: 'Comfortable indoor lighting',   color: '#ca8a04', percent: 55 }
  if (lux < 2000) return { label: 'Bright', desc: 'Bright indoor or overcast day', color: '#eab308', percent: 80 }
  return { label: 'Very Bright', desc: 'Direct sunlight or intense light', color: '#fde047', percent: 100 }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LightSensorModalClassic({
  device,
  deviceState,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const rawLux = numAttr(deviceState, 'luxLevel', 'lux', 'illuminance')
  const displayLux = rawLux ?? (isEditorMode ? 320 : null)
  const lightLevel = useMemo(() => displayLux !== null ? getLightLevel(displayLux) : null, [displayLux])

  return (
    <div
      className={`light-sensor-modal-classic${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="light-sensor-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="light-sensor-modal-classic__header">
        <button className="light-sensor-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="light-sensor-modal-classic__title" data-modal-slot="header-title">{device.name || 'Light Sensor'}</h2>
        <button
          className="light-sensor-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'lux':
            return (
              <div key="lux" className={`light-sensor-modal-classic__lux${hl}`} data-modal-section="lux">
                <div className="light-sensor-modal-classic__section-label" data-modal-slot="lux-label">ILLUMINANCE</div>
                <div className="light-sensor-modal-classic__lux-display">
                  <span
                    className="material-symbols-rounded light-sensor-modal-classic__lux-icon"
                    data-modal-slot="lux-icon"
                    style={lightLevel ? { color: lightLevel.color } : undefined}
                  >
                    light_mode
                  </span>
                  <div className="light-sensor-modal-classic__lux-number">
                    <span className="light-sensor-modal-classic__lux-value" data-modal-slot="lux-value">
                      {displayLux !== null ? displayLux.toFixed(0) : '—'}
                    </span>
                    <span className="light-sensor-modal-classic__lux-unit" data-modal-slot="lux-unit">lux</span>
                  </div>
                </div>
              </div>
            )
          case 'level':
            return (
              <div key="level" className={`light-sensor-modal-classic__level${hl}`} data-modal-section="level">
                <div className="light-sensor-modal-classic__section-label" data-modal-slot="level-label">LIGHT LEVEL</div>
                {lightLevel ? (
                  <>
                    <div className="light-sensor-modal-classic__level-row">
                      <span
                        className="light-sensor-modal-classic__level-badge"
                        data-modal-slot="level-badge"
                        style={{ color: lightLevel.color, borderColor: lightLevel.color + '44' }}
                      >
                        {lightLevel.label}
                      </span>
                      <span className="light-sensor-modal-classic__level-desc" data-modal-slot="level-desc">
                        {lightLevel.desc}
                      </span>
                    </div>
                    <div className="light-sensor-modal-classic__level-bar-track">
                      <div
                        className="light-sensor-modal-classic__level-bar-fill"
                        data-modal-slot="level-bar-fill"
                        style={{ width: `${lightLevel.percent}%`, background: lightLevel.color }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="light-sensor-modal-classic__empty">No lux data available</div>
                )}
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
